/**
 * SmsListPicker — searchable single-select for choosing an SMS subscriber
 * list (broadcast target, keyword join/leave target, folder-notify list).
 * Mirrors ContactPicker: "None" option clears, empty state links to the
 * management page.
 *
 * Per CLAUDE.md rule 13: never a raw UUID input — this picker is the
 * canonical way to link an SMS list from the portal.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { smsLists, type SmsListRow } from '@/api/smsLists';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { formatRef } from '@/lib/formatRef';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { MessageSquareText, ExternalLink } from 'lucide-react';

const NONE_VALUE = '__none__';

export function SmsListPicker({
  value,
  onChange,
  label,
  allowNone = true,
  noneLabel = '— None —',
  emptyHint,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  label?: string;
  /** Show the clear option (default true). Required pickers pass false. */
  allowNone?: boolean;
  noneLabel?: string;
  emptyHint?: string;
}) {
  const customerId = useEffectiveCustomerId();
  const { data, isLoading } = useQuery({
    queryKey: ['sms-lists', 'picker', customerId],
    queryFn: () => smsLists.list({
      customer_id: customerId ?? undefined,
      limit: 500,
    }),
  });
  const rows: SmsListRow[] = data?.rows ?? [];

  if (!isLoading && rows.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border bg-secondary/20 p-3 space-y-1.5">
        <p className="text-xs text-muted-foreground">
          {emptyHint ?? 'No SMS lists exist yet for this account.'}
        </p>
        <Link
          to="/sms"
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          Create one under SMS &rarr; Lists
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {label && (
        <label className="text-xs font-medium text-muted-foreground block">
          {label}
        </label>
      )}
      <Select
        value={value ?? NONE_VALUE}
        onValueChange={(v) => onChange(v === NONE_VALUE ? null : v)}
        disabled={isLoading}
      >
        <SelectTrigger className="w-full h-8 text-sm">
          <SelectValue placeholder={isLoading ? 'Loading lists…' : 'Choose a list'} />
        </SelectTrigger>
        <SelectContent>
          {allowNone && (
            <SelectItem value={NONE_VALUE}>
              <span className="text-muted-foreground">{noneLabel}</span>
            </SelectItem>
          )}
          {rows.map((r) => (
            <SelectItem key={r.id} value={r.id}>
              <span className="inline-flex items-center gap-2">
                <MessageSquareText className="h-3.5 w-3.5 opacity-60" />
                <span>{r.name}</span>
                <span className="font-mono text-[10px] text-muted-foreground">
                  {formatRef(r.ref_number, 'SML')}
                </span>
                {typeof r.member_count === 'number' && (
                  <span className="text-[10px] text-muted-foreground tabular-nums">
                    {r.member_count} subscriber{r.member_count === 1 ? '' : 's'}
                  </span>
                )}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
